import React, { ReactElement } from 'react'
import { Button } from '@mui/material'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  AccreditationKey,
  clearCourseAccreditationNotPursued,
  clearEventAccreditationNotPursued,
  selectAccreditationNotPursuedToggleStatus,
  setCourseAccreditationNotPursued,
  setEventAccreditationNotPursued,
} from 'src/v3/content-pipeline/accreditation-health.slice'

type ContentDomain = 'live' | 'enduring'

type Props = {
  domain: ContentDomain
  rowId: string
  slot: AccreditationKey
  isFlagged: boolean
}

// Both POST (flag) and DELETE (unflag) are idempotent on the backend, so
// there's no need to read current flag state first. The dashboard refetch
// that follows a successful toggle is handled by listener middleware (see
// pipeline.middleware.ts). Loading state is read from the slice's own
// per-toggle status, not by awaiting/unwrapping the dispatch here.
export default function AccreditationNotPursuedToggle({
  domain,
  rowId,
  slot,
  isFlagged,
}: Props): ReactElement {
  let dispatch = useAppDispatch()
  let toggleStatus = useAppSelector(
    selectAccreditationNotPursuedToggleStatus(rowId, slot),
  )
  let isSaving = toggleStatus === 'pending'

  let handleToggle = () => {
    if (domain === 'live' && isFlagged) {
      dispatch(clearEventAccreditationNotPursued({ eventID: rowId, slot }))
    } else if (domain === 'live') {
      dispatch(setEventAccreditationNotPursued({ eventID: rowId, slot }))
    } else if (isFlagged) {
      dispatch(clearCourseAccreditationNotPursued({ courseID: rowId, slot }))
    } else {
      dispatch(setCourseAccreditationNotPursued({ courseID: rowId, slot }))
    }
  }

  return (
    <Button
      size="small"
      variant="text"
      disabled={isSaving}
      onClick={(e) => {
        e.stopPropagation()
        handleToggle()
      }}
      onKeyDown={(e) => e.stopPropagation()}
      aria-label={
        isFlagged
          ? `Unflag ${slot} as not pursued`
          : `Mark ${slot} as not pursued`
      }
    >
      {isSaving ? 'Saving...' : isFlagged ? 'Unflag' : 'Not pursued'}
    </Button>
  )
}
